import { useEffect } from "react"
import { matchPath, useLocation } from "react-router-dom"
import { SidebarData } from "../core/data/json/sidebarData"
import { all_routes } from "../feature-module/router/all_routes"
import { reportPageView } from "../service/activity-log"

// Reports each page the signed-in user opens to the backend's Activity Log.
// One small request per navigation, never awaited.

const humanize = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

// Menu label for a path, e.g. "/user-management/manage-users" -> "Users".
// Built on first use: the sidebar is the wording staff actually see.
let sidebarTitles: Map<string, string> | null = null

const getSidebarTitles = (): Map<string, string> => {
  if (sidebarTitles) return sidebarTitles
  const titles = new Map<string, string>()
  const walk = (items: any[]) => {
    items.forEach((item) => {
      if (typeof item?.link === "string" && item.link.startsWith("/") && item.label) {
        if (!titles.has(item.link)) titles.set(item.link, item.label)
      }
      if (Array.isArray(item?.submenuItems)) walk(item.submenuItems)
    })
  }
  walk(SidebarData as any[])
  sidebarTitles = titles
  return titles
}

const isId = (segment: string): boolean =>
  /^[a-f0-9]{24}$/i.test(segment) || /^\d+$/.test(segment)

export const pageTitleFor = (pathname: string): string => {
  const fromSidebar = getSidebarTitles().get(pathname)
  if (fromSidebar) return fromSidebar

  // A route with params ("/rms/report-card-designer/:id") is named after its
  // key in all_routes.
  const route = Object.entries(all_routes).find(
    ([, pattern]) => pattern === pathname || !!matchPath({ path: pattern, end: true }, pathname)
  )
  if (route) return humanize(route[0])

  // Anything else (nested tabs such as a student's Fees tab): name it from the
  // path, leaving record ids out.
  const segments = pathname.split("/").filter((s) => s && !isId(s))
  return segments.slice(-2).map(humanize).join(" › ")
}

// React StrictMode runs effects twice in development, and some screens
// replace the URL as they settle; don't report the same page twice in a row.
const REPEAT_WINDOW_MS = 2000
// A page the user is not allowed to open redirects to /unauthorized at once.
// Wait a moment before reporting, so a URL they were bounced from is never
// recorded as a page they opened.
const SETTLE_MS = 400
let last = { path: "", at: 0 }

// Some pages (admission and registration details, the admission edit form) are
// handed their record through router state rather than the URL. Its id goes
// along with the page view so the log can say whose record was opened.
const recordIdOf = (state: unknown): string => {
  const id = (state as { _id?: unknown } | null)?._id
  return typeof id === "string" && /^[a-f0-9]{24}$/i.test(id) ? id : ""
}

export const usePageViewTracking = (): void => {
  const { pathname, state } = useLocation()
  const recordId = recordIdOf(state)

  useEffect(() => {
    const timer = setTimeout(() => {
      const now = Date.now()
      const key = `${pathname}#${recordId}`
      if (last.path === key && now - last.at < REPEAT_WINDOW_MS) return
      last = { path: key, at: now }
      reportPageView(pathname, pageTitleFor(pathname), recordId || undefined)
    }, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [pathname, recordId])
}
